import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { ChevronLeft, Download, Users } from 'lucide-react'
import QRCode from 'qrcode'
import html2canvas from 'html2canvas'

const Container = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.backgroundColor || '#ffffff'};
  color: ${props => props.theme.textPrimary || '#000000'};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
`

const Header = styled.div`
  width: 100%;
  max-width: 800px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 40px;
`

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textPrimary || '#000000'};
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  
  &:hover {
    opacity: 0.8;
  }
`

const Logo = styled.div`
  width: 150px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 150px;
    height: 150px;
    object-fit: contain;
  }
`

const ProgressBar = styled.div`
  width: 100%;
  max-width: 600px;
  display: flex;
  gap: 20px;
  margin-bottom: 60px;
`

const ProgressStep = styled.div`
  flex: 1;
  height: 4px;
  background-color: ${props => props.$active ? '#ff4757' : props.$completed ? '#fff' : '#2c3e50'};
  border-radius: 2px;
  transition: all 0.3s ease;
`

const ContentArea = styled.div`
  width: 100%;
  max-width: 800px;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

// Step 1 Components
const ObjectCard = styled.div`
  background: white;
  color: #333;
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  margin-bottom: 40px;
`

const ObjectCount = styled.h2`
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 20px 0;
`

const LoadingText = styled.p`
  font-size: 20px;
  margin: 40px 0;
  text-align: center;
  color: ${props => props.theme.textPrimary || '#000000'};
`

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid ${props => props.theme.backgroundColor === '#0f1b2e' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
  border-top-color: ${props => props.theme.backgroundColor === '#0f1b2e' ? 'white' : '#333333'};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 20px auto;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`

// Step 2 Components
const PrintCard = styled.div`
  background: white;
  color: #333;
  border-radius: 20px;
  padding: 30px;
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
  width: 100%;
  max-width: 400px;
`

const PrintIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ffffff;
  border-radius: 12px;
  margin-left:10px;
  margin-right:7px;
  margin-top:2px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
  }
`

const PrintInfo = styled.div`
  flex: 1;
`

const PrintTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 4px 0;
`

const PrintDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`

const ActionButton = styled.button`
  background: ${props => props.$primary ? '#ff4757' : 'transparent'};
  color: ${props => props.$primary ? 'white' : '#333'};
  border: ${props => props.$primary ? 'none' : '2px solid #ff4757'};
  padding: 16px 40px;
  border-radius: 30px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 71, 87, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const InfoText = styled.p`
  text-align: center;
  font-size: 18px;
  margin-bottom: 40px;
  opacity: 0.9;
  color: ${props => props.theme.textPrimary || '#000000'};
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: center;
`

// Step 3 Components
const ModeCard = styled.div`
  background: ${props => props.$selected ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.85)'};
  color: #333;
  border-radius: 20px;
  padding: 40px 30px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 280px;
  border: ${props => props.$selected ? '3px solid #ff4757' : '3px solid transparent'};
  
  &:hover {
    background: rgba(255, 255, 255, 0.95);
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`

const ModeIcon = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100px;
    height: 100px;
    object-fit: contain;
  }
`

const ModeTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 15px 0;
`

const ModeDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.6;
`

const ModeSelection = styled.div`
  display: flex;
  gap: 40px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`

const EndConditionInput = styled.input`
  background: white;
  border: 1px solid #ddd;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 16px;
  width: 200px;
  text-align: center;
  margin: 20px auto;
  display: block;
`

const QRGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
  padding: 40px;
  background: white;
  border-radius: 20px;
  margin: 20px 0;
  max-width: 800px;
  width: 100%;
`

const QRItem = styled.div`
  text-align: center;
  padding: 20px;
  
  img {
    width: 100px;
    height: 100px;
    margin-bottom: 10px;
  }
  
  p {
    margin: 0;
    font-size: 14px;
    color: #333;
    font-weight: 600;
  }
`

export default function GameReadyPage() {
  const { theme } = useTheme()
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [currentStep, setCurrentStep] = useState(1)
  const [objectCount, setObjectCount] = useState(0)
  const [problemSetId, setProblemSetId] = useState(null)
  const [problems, setProblems] = useState([])
  const [qrCodesGenerated, setQrCodesGenerated] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [pdfViewed, setPdfViewed] = useState(false)
  const [selectedMode, setSelectedMode] = useState(null)
  const [endCondition, setEndCondition] = useState('')
  const [qrCodes, setQrCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [progressMode, setProgressMode] = useState('sequential')  // 게임 진행 방식: 'sequential' | 'qr_scan' (기본값: 순서 모드)

  // ★ 인쇄용 QR이 실제 게임방과 같은 roomCode/objectName 을 갖도록
  //   GameReadyPage 진입 직후 세션을 미리 생성한다.
  const [preparedSession, setPreparedSession] = useState(null) // { roomCode, objects, ... }
  const preparedSessionRef = useRef(null)
  const transferredRef = useRef(false) // WaitingRoom 으로 정상 이동했는지 (true 면 cleanup 에서 세션 삭제 안 함)

  useEffect(() => {
    preparedSessionRef.current = preparedSession
  }, [preparedSession])

  // 데이터베이스에서 문제 세트 정보 조회 + 세션 미리 생성
  useEffect(() => {
    let cancelled = false

    const init = async () => {
      const problemSet = location.state?.problemSet
      if (!problemSet || !problemSet.id) {
        console.error('문제 세트 정보가 없습니다.')
        setObjectCount(location.state?.objectCount || 12)
        setLoading(false)
        return
      }

      console.log('📋 문제 세트 ID:', problemSet.id)
      if (!cancelled) setProblemSetId(problemSet.id)

      // 1) 퀴즈 상세 조회로 set_obj_count / problems 확보
      try {
        const detailRes = await fetch(`/auth/get-quiz-detail/${problemSet.id}`, {
          credentials: 'include'
        })
        if (detailRes.ok) {
          const result = await detailRes.json()
          if (result.success && result.quiz) {
            const actualObjectCount = result.quiz.set_obj_count || result.quiz.problems?.length || 0
            console.log('📊 데이터베이스에서 조회한 set_obj_count:', actualObjectCount)
            if (!cancelled) {
              setObjectCount(actualObjectCount)
              setProblems(result.quiz.problems || [])
            }
          } else {
            if (!cancelled) setObjectCount(location.state?.objectCount || 12)
          }
        } else {
          if (!cancelled) setObjectCount(location.state?.objectCount || 12)
        }
      } catch (error) {
        console.error('문제 세트 정보 조회 오류:', error)
        if (!cancelled) setObjectCount(location.state?.objectCount || 12)
      }

      // 2) 세션 미리 생성 (실제 roomCode + 실제 object 목록 확보)
      try {
        const coverImageUrl =
          problemSet.set_cover_img || problemSet.image || problemSet.cover_image || null

        // 기본값: 게임방, qr_scan 모드, time_limit 15분.
        // Step 3 에서 사용자가 mode/end 를 고른 뒤에 PUT /sessions/:code/settings 로 덮어쓴다.
        const res = await fetch(`/api/sessions/from-quiz/${problemSet.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            coverImage: coverImageUrl,
            roomType: 'game',
            progressMode: 'qr_scan',
            endCondition: 'time_limit',
            timeLimitMinutes: 15,
            hostUserId: user?.username
          })
        })
        const data = await res.json()
        if (res.ok && data?.success && data.session?.roomCode) {
          if (cancelled) {
            // 이미 페이지를 떠난 상태 → 서버에 남은 빈 세션 즉시 삭제
            console.log(`🧹 init 도중 unmount — 미사용 세션 ${data.session.roomCode} 삭제`)
            fetch(`/api/sessions/${encodeURIComponent(data.session.roomCode)}`, {
              method: 'DELETE',
              credentials: 'include',
              keepalive: true
            }).catch(() => {})
          } else {
            console.log('✅ GameReady 세션 미리 생성:', data.session.roomCode)
            setPreparedSession(data.session)
          }
        } else {
          console.warn('⚠️ 세션 미리 생성 실패 — 인쇄 QR 은 임시 코드로 생성됩니다.')
        }
      } catch (err) {
        console.error('세션 미리 생성 오류:', err)
      }

      if (!cancelled) setLoading(false)
    }

    init()

    return () => {
      cancelled = true
    }
  }, [location.state, user?.username])

  // 페이지 떠날 때 (뒤로가기 / 다른 탭 / 새로고침 등)
  // WaitingRoom 으로 정상 이동한 경우가 아니면 미리 만든 빈 세션을 삭제한다.
  useEffect(() => {
    return () => {
      if (transferredRef.current) return
      const code = preparedSessionRef.current?.roomCode
      if (!code) return
      console.log(`🧹 GameReady cleanup — 빈 세션 ${code} 삭제 요청`)
      fetch(`/api/sessions/${encodeURIComponent(code)}`, {
        method: 'DELETE',
        credentials: 'include',
        keepalive: true
      }).catch(err => console.warn('세션 삭제 실패:', err))
    }
  }, [])

  // Step 1: Generate QR Codes (세션 준비 완료된 뒤 실행)
  useEffect(() => {
    if (currentStep === 1 && !qrCodesGenerated && !loading && objectCount > 0) {
      generateQRCodes()
    }
    // preparedSession 이 늦게 도착할 수 있으므로 의존성에 포함
  }, [currentStep, qrCodesGenerated, loading, objectCount, preparedSession])

  const generateQRCodes = async () => {
    // 우선순위: 미리 생성된 세션의 objects > problems 의 ar_object_name 추출 > Object_N fallback
    const sessionRoomCode = preparedSession?.roomCode || 'PREVIEW'
    let objectNames = []

    if (Array.isArray(preparedSession?.objects) && preparedSession.objects.length > 0) {
      objectNames = preparedSession.objects.map(o => o?.name).filter(Boolean)
    } else if (Array.isArray(problems) && problems.length > 0) {
      const seen = new Set()
      for (const p of problems) {
        const name = p?.ar_object_name || p?.objectName
        if (name && !seen.has(name)) {
          seen.add(name)
          objectNames.push(name)
        }
      }
    }

    // 그래도 못 구하면 objectCount 만큼 임시 이름 — Unity 인식 불가하지만 레이아웃은 유지
    if (objectNames.length === 0) {
      for (let i = 1; i <= objectCount; i++) objectNames.push(`Object_${i}`)
    }

    const codes = []
    for (let i = 0; i < objectNames.length; i++) {
      const payload = {
        type: 'unlock-object',
        roomCode: sessionRoomCode,
        objectName: objectNames[i],
        objectIndex: i
      }
      const qrData = JSON.stringify(payload)
      const qrCode = await QRCode.toDataURL(qrData, {
        width: 200,
        margin: 2,
        errorCorrectionLevel: 'M',
        color: { dark: '#000000', light: '#FFFFFF' }
      })
      codes.push({
        id: i + 1,
        name: objectNames[i],
        data: payload,
        image: qrCode
      })
    }
    setQrCodes(codes)

    setTimeout(() => {
      setQrCodesGenerated(true)
      setTimeout(() => {
        setCurrentStep(2)
      }, 1500)
    }, 2000)
  }

  // Step 2: Create downloadable QR codes
  const downloadQRCodes = async () => {
    const qrContainer = document.getElementById('qr-grid')
    if (qrContainer) {
      const canvas = await html2canvas(qrContainer, {
        backgroundColor: '#ffffff',
        scale: 2
      })
      
      const link = document.createElement('a')
      link.download = `AR_QR_Codes_${Date.now()}.png`
      link.href = canvas.toDataURL()
      link.click()
      
      const url = canvas.toDataURL()
      setPdfUrl(url)
      
      // Simulate PDF viewing
      setTimeout(() => {
        setPdfViewed(true)
      }, 2000)
    }
  }

  const handlePrintComplete = () => {
    setCurrentStep(3)
  }

  const handleModeSelect = (mode) => {
  setSelectedMode(mode)
  if (mode === 'time') {
    setEndCondition('15')
  } else if (mode === 'money') {
    setEndCondition('100000')
  }
}

  // 미리 만들어둔 세션에 최종 모드/종료조건만 덮어쓰고 WaitingRoom 으로 이동.
  // 세션이 없으면 (네트워크 실패 등) 기존처럼 location.state.room 만 들고 이동 → WaitingRoom 이 새 세션 생성.
  const moveToWaitingRoom = async () => {
    const mode = selectedMode || 'time'
    const end = endCondition || '15'
    const fallbackSet = location.state?.problemSet

    const gameOptions = {
      roomType: 'game',
      progressMode: progressMode,
      endCondition: mode === 'time' ? 'time_limit' : 'score_target',
      timeLimitMinutes: mode === 'time' ? parseInt(end) : undefined,
      scoreTarget: mode === 'money' ? parseInt(end) : undefined,
      hostUserId: user?.username
    }

    let roomForState = null
    if (preparedSession?.roomCode) {
      // 미리 만든 세션에 모드 반영
      try {
        const settingsRes = await fetch(
          `/api/sessions/${encodeURIComponent(preparedSession.roomCode)}/settings`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              roomType: gameOptions.roomType,
              progressMode: gameOptions.progressMode,
              endCondition: gameOptions.endCondition,
              timeLimitMinutes: gameOptions.timeLimitMinutes,
              scoreTarget: gameOptions.scoreTarget
            })
          }
        )
        if (!settingsRes.ok) {
          console.warn('⚠️ 세션 설정 업데이트 실패 — 새 세션이 만들어질 수 있음')
        }
      } catch (err) {
        console.warn('세션 설정 업데이트 오류:', err)
      }

      roomForState = {
        roomCode: preparedSession.roomCode,
        // socketUrl 은 일부러 null. WaitingRoomPage 가
        //   `${window.location.origin}/sessions`
        // 로 fallback 해서 정확한 /sessions namespace 를 연결한다.
        // preparedSession.socketUrl 은 namespace 접미사가 없어서 그대로 넘기면 host 가 잘못된
        // 네임스페이스로 연결되고 player:joined 이벤트를 못 받음.
        socketUrl: null,
        joinQrUrl: null
      }
      // WaitingRoom cleanup 이 빈 세션을 지우지 않도록 transferred 마킹
      transferredRef.current = true
    }

    navigate('/waiting-room', {
      state: {
        ...gameOptions,
        mode,
        endCondition: end,
        qrCodes: qrCodes,
        problemSet: fallbackSet,
        room: roomForState,
        // WaitingRoom 에 "이미 만든 세션을 그대로 써라" 신호
        skipSessionCreate: !!roomForState,
        fixedQrs: null
      }
    })
  }

  const handleStartGame = () => {
    if (!selectedMode || !endCondition) return
    moveToWaitingRoom()
  }

  // Quick start: skip printing and go straight to waiting room (session QR)
  const handleQuickStart = () => {
    moveToWaitingRoom()
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    } else {
      navigate(-1)
    }
  }

  return (
    <Container theme={theme}>
      <Header>
        <BackButton theme={theme} onClick={handleBack}>
          <ChevronLeft size={20} />
          뒤로
        </BackButton>
        <Logo>
          <img src="/logo.png" alt="AR 대탈출" />
        </Logo>
        <div style={{ width: '60px' }} />
      </Header>

      <ProgressBar>
        <ProgressStep $completed={currentStep > 1} $active={currentStep === 1} />
        <ProgressStep $completed={currentStep > 2} $active={currentStep === 2} />
        <ProgressStep $completed={false} $active={currentStep === 3} />
      </ProgressBar>

      <ContentArea>
        {currentStep === 1 && (
          <>
            {loading ? (
              <>
                <LoadingText theme={theme}>
                  문제 세트 정보를 조회하고 있습니다...
                </LoadingText>
                <LoadingSpinner theme={theme} />
              </>
            ) : (
              <>
                <ObjectCard>
                  <ObjectCount>{objectCount} Objects</ObjectCount>
                </ObjectCard>
                
                <LoadingText theme={theme}>
                  {objectCount}개의 오브젝트에 대한<br />
                  QR 코드를 생성합니다.
                </LoadingText>

                {!qrCodesGenerated && <LoadingSpinner theme={theme} />}
              </>
            )}
          </>
        )}

        {currentStep === 2 && (
          <>
            <PrintCard>
              <PrintIcon>
                <img src="/printqr.png" alt="QR 인쇄" />
              </PrintIcon>
              <PrintInfo>
                <PrintTitle>인쇄하기</PrintTitle>
                <PrintDescription>{objectCount}개의 QR코드를 인쇄하세요.</PrintDescription>
              </PrintInfo>
            </PrintCard>

            <InfoText theme={theme}>
              QR 코드를 인쇄하여<br />
              교실에 붙여주세요!
            </InfoText>

            <QRGrid id="qr-grid">
              {qrCodes.map(qr => (
                <QRItem key={qr.id}>
                  <img src={qr.image} alt={`QR Code ${qr.name || qr.id}`} />
                  <p>{qr.name || `Object ${qr.id}`}</p>
                </QRItem>
              ))}
            </QRGrid>

            {!pdfUrl && (
              <ButtonGroup>
                <ActionButton $primary onClick={downloadQRCodes}>
                  <Download size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  QR 코드 다운로드
                </ActionButton>
                <ActionButton onClick={handlePrintComplete}>
                  스킵
                </ActionButton>
              </ButtonGroup>
            )}

            {pdfViewed && (
              <ActionButton $primary onClick={handlePrintComplete}>
                인쇄 완료
              </ActionButton>
            )}
          </>
        )}

        {currentStep === 3 && (
          <>
            {/* ★★★ 게임 진행 방식 선택 */}
            <div style={{ marginBottom: '40px', width: '100%', maxWidth: '800px' }}>
              <h3 style={{ textAlign: 'center', marginBottom: '20px', color: '#fff' }}>
                게임 진행 방식
              </h3>
              <ModeSelection>
                <ModeCard
                  $selected={progressMode === 'sequential'}
                  onClick={() => setProgressMode('sequential')}
                >
                  <ModeTitle>순서 모드</ModeTitle>
                  <ModeDescription>
                    문제를 풀면 자동으로<br />
                    다음 오브젝트가 잠금 해제됩니다.
                  </ModeDescription>
                </ModeCard>

                <ModeCard
                  $selected={progressMode === 'qr_scan'}
                  onClick={() => setProgressMode('qr_scan')}
                >
                  <ModeTitle>QR 스캔 모드</ModeTitle>
                  <ModeDescription>
                    QR 코드를 스캔해야<br />
                    다음 오브젝트가 잠금 해제됩니다.
                  </ModeDescription>
                </ModeCard>
              </ModeSelection>
            </div>

            {/* ★★★ 게임 종료 조건 선택 */}
            <div style={{ width: '100%', maxWidth: '800px' }}>
              <h3 style={{ textAlign: 'center', marginBottom: '20px', color: '#fff' }}>
                게임 종료 조건
              </h3>
              <ModeSelection>
                <ModeCard
                  $selected={selectedMode === 'time'}
                  onClick={() => handleModeSelect('time')}
                >
                  <ModeIcon>
                    <img src="/time.png" alt="시간 모드" />
                  </ModeIcon>
                  <ModeTitle>시간</ModeTitle>
                  <ModeDescription>
                    호스트가 설정한 시간이<br />
                    지났을 때 방탈출이 끝납니다.
                  </ModeDescription>
                </ModeCard>

                <ModeCard
                  $selected={selectedMode === 'money'}
                  onClick={() => handleModeSelect('money')}
                >
                  <ModeIcon>
                    <img src="/point.png" alt="포인트 모드" />
                  </ModeIcon>
                  <ModeTitle>머니</ModeTitle>
                  <ModeDescription>
                    한 플레이어가<br />
                    머니 목표치를 달성했을 때<br />
                    방탈출이 끝납니다.
                  </ModeDescription>
                </ModeCard>
              </ModeSelection>
            </div>

            {selectedMode && (
              <>
                <EndConditionInput
                  type="number"
                  placeholder={selectedMode === 'time' ? '시간(분)' : '목표 머니'}
                  value={endCondition}
                  onChange={(e) => setEndCondition(e.target.value)}
                />
                
                {selectedMode === 'money' && (
                  <InfoText style={{ fontSize: '16px', opacity: 0.8 }}>
                    {endCondition || '0'} 머니 달성 시 종료하기
                  </InfoText>
                )}
                
                {selectedMode === 'time' && (
                  <InfoText style={{ fontSize: '16px', opacity: 0.8 }}>
                    {endCondition || '0'} 분 달성 시 종료하기
                  </InfoText>
                )}
              </>
            )}

            <ActionButton 
              $primary 
              onClick={handleStartGame}
              disabled={!selectedMode || !endCondition}
            >
              <Users size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              초대하기
            </ActionButton>
          </>
        )}
      </ContentArea>
    </Container>
  )
}






